/**
 * Module for showing Inventory Details.
 * @module src/InventoryRequest/InventoryRequestDetail
 */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useParams, withRouter } from 'react-router-dom';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Spinner from '@salesforce/design-system-react/components/spinner';
import { useSelector, useDispatch } from 'react-redux';
import CancelInventory from '../InventoryRequestCancel';
import InventoryRequestDetailHeader from './InventoryRequestDetailHeader';
import InventoryRequestDetailCatalog from './InventoryRequestDetailCatalog';
import CANCEL_INVENTORY from '../../../graphql/mutations/cancelInventory';
import SUBMIT_INVENTORY_REQUEST from '../../../graphql/mutations/submitInventoryRequest';
import RESUBMIT_ORDER_DETAILS from '../../../graphql/mutations/resubmitOrderDetails';
import { GET_INVENTORY_REQUEST_DETAILS } from '../../../graphql/getInventoryDetails';
import { GET_ORDER_DETAILS } from '../../../graphql/getOrderDetails';
import DetailsHeader from './DetailsHeader';
import Comments from './Comments';
import './index.scss';
import InventoryRequestComponent from '../InventoryRequest';
import { GET_FORM_CONFIGURATIONS } from '../../../graphql/getFormConfigurations';
import CancelEventModal from '../../CreateEditSurgeryEvent/CancelEventCreationModal';
import { getUserInfo, setUserInfo } from '../../../store/ducks/userInfo';
import SnackBar from '../../Shared/SnackBar';
import useSnackBar from '../../../util/customHooks/useSnackBar';
import {
  INVENTORY_REQUEST_DETAIL_ACTIONS,
  INVENTORY_REQUEST_ASYNC_MESSAGES,
  INVENTORY_REQUEST_DETAIL_MESSAGES,
  INVENTORY_REQUEST_DETAIL_PAYLOAD,
  INVENTORY_REQUEST_DETAIL_ROUTES,
  INVENTORY_REQUEST_SUBMISSION_STATUS,
  INVENTORY_REQUEST_DETAIL_UI,
  NOTIFY_TYPE,
  ORDER_SCREEN_TYPE,
  OPS_ACTION_TYPE,
  PERSONA_CONDITION,
  STALE_RECORD_MESSAGE,
  SYSTEM_NAME,
  DEFAULT_VERSION,
} from '../../../util/constants';
import { isStaleRecordError } from '../../../util/optimisticLock';
import { getUserId } from '../../../store/ducks/userId';
import { GET_USER_INFO } from '../../../graphql/getUserInfoData';
import { GET_WORKFLOW_PERMISSIONS } from '../../../graphql/workflowPermissions';
import CREATE_UPDATE_INVENTORY_FAVORITE from '../../../graphql/mutations/createUpdateInventoryFavorites';
import InventoryFavoriteSaveModal from './FavoriteSaveConfirmationModal';
import { warningValidation } from '../../../util/utilityFunctions';
import { GET_DETAILS_VIEW_CONFIGURATIONS } from '../../../graphql/getDetailsViewConfigurations';
import CREATE_UPDATE_USAGE from '../../../graphql/mutations/createdUpdateUsage';
import GET_SUBMISSION_STATUS from '../../../graphql/getSubmissionStatus';
import GET_SUBMISSION_STATUS_BY_CASE from '../../../graphql/getSubmissionStatusByCase';
import GET_VALIDATE_INVENTORY_REQUEST_SUBMIT from '../../../graphql/getValidateInventoryRequestSubmit';
import { isSubmissionInFlight, isRecentSubmission } from '../../../util/submissionStatus';

const DetailsHeaderComponent = (DetailsHeader as unknown) as React.ComponentType<
  Record<string, unknown>
>;
const InventoryRequest = (InventoryRequestComponent as unknown) as React.ComponentType<
  Record<string, unknown>
>;

export interface Props extends RouteComponentProps {
  children?: FC;
  eventInventoryData?: any;
  eventInventoryExternalId?: string;
  hasEventInventory?: boolean;
  tabConfig?: any;
}
type ReturnParams = {
  externalId: string;
  type: string;
  id: string;
  isEventInventoryFlow: string | undefined;
  caseExternalId: string | undefined;
};

type ValidationReason = {
  code?: string;
  message?: string;
};

type ValidateInventorySubmitResult = {
  valid?: boolean;
  reasons?: ValidationReason[];
  isOpsApprovalRequired?: boolean;
  is3plOpsApprovalRequired?: boolean;
};

type SubmitInventoryMutationResult = {
  data?: {
    submitInventoryRequest?: {
      submissionId?: string | null;
      status?: string | null;
    };
  };
};

const InventoryRequestDetail: FC<Props> = ({
  eventInventoryData = null,
  location,
  eventInventoryExternalId,
  history,
  hasEventInventory,
  tabConfig,
}) => {
  const { state }: any = location;
  const apolloClient = useApolloClient();
  const useParam = useParams<ReturnParams>();
  const parameters: any = state || useParam;
  const { externalId, type, id, isEventInventoryFlow } = parameters;
  useQuery(GET_WORKFLOW_PERMISSIONS);
  const dispatch = useDispatch();
  const userInfo = useSelector(getUserInfo);
  const [loader, setLoader] = useState(false);
  const [checkStatusActive, setCheckStatusActive] = useState(false);
  const userId = useSelector(getUserId);
  const [userComment, setUserComment] = useState<any[]>();
  const [shipMethod, setShipMethod] = useState('');
  const { open, notification, openSnackBar } = useSnackBar();
  const [openInventoryCancelModal, setOpenInventoryCancelModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isClone, setIsClone] = useState(false);
  const [isSave, setIsSave] = useState(false);
  const [FavName, setFavName] = useState('');
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [reprocessQueue, setReprocessQueue] = useState(false);
  const [selectedTabIndex, changeSelectedTab] = useState(0);
  const submitInFlightRef = useRef(false);
  // Latched to true when classifySubmitError returns canRetry: false.
  // Prevents re-submit after fatal errors (dirty state, permission denied).
  const submitRetryBlockedRef = useRef(false);
  const lastSuccessToastKeyRef = useRef<string | null>(null);
  const submitWarningToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitSuccessToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentSubmissionId, setCurrentSubmissionId] = useState<string | null>(null);
  const [submissionState, setSubmissionState] = useState<'idle' | 'pending' | 'success' | 'error'>(
    'idle'
  );
  const [createUpdateUsage] = useMutation(CREATE_UPDATE_USAGE);

  const [getFormConfigurations, { data: formdata }] = useLazyQuery(GET_FORM_CONFIGURATIONS);
  const { data } = useQuery(GET_USER_INFO, {
    fetchPolicy: 'no-cache',
    variables: { id: userId },
  });
  const { data: getDetailsViewConfigurations } = useQuery(GET_DETAILS_VIEW_CONFIGURATIONS);
  const inventoryConfig =
    getDetailsViewConfigurations?.getDetailsViewConfigurations?.inventoryRequest;

  useEffect(() => {
    getFormConfigurations();
  }, [getFormConfigurations]);

  const {
    data: getOrderDetails,
    refetch: refetchProcessedEventDetails,
    loading: processedLoading,
  } = useQuery(GET_ORDER_DETAILS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: checkStatusActive,
    variables: {
      id: externalId,
      orderType: INVENTORY_REQUEST_DETAIL_PAYLOAD.ORDER_TYPE_INVENTORY_REQUEST,
    },
  });

  const { data: getInventoryRequestDetails, refetch: refetchEventDetails, loading } = useQuery(
    GET_INVENTORY_REQUEST_DETAILS,
    {
      fetchPolicy: 'no-cache',
      variables: {
        externalId:
          isEventInventoryFlow || hasEventInventory ? eventInventoryExternalId : externalId,
      },
    }
  );
  const [
    setCancelInventory,
    { loading: cancelInventoryLoading, data: cancelInventoryData },
  ] = useMutation(CANCEL_INVENTORY);

  const [setSubmitInventory, { loading: submitInventoryLoading }] = useMutation(
    SUBMIT_INVENTORY_REQUEST
  );

  let reconciliationCaseExternalId = '';

  if (type === 'Processed' || type === 'Failed') {
    reconciliationCaseExternalId = getOrderDetails?.getOrderDetails?.caseExternalId || '';
  } else if (eventInventoryData && externalId === eventInventoryExternalId) {
    reconciliationCaseExternalId = eventInventoryData?.getInventoryRequestDetails?.caseExternalId;
  } else {
    reconciliationCaseExternalId =
      getInventoryRequestDetails?.getInventoryRequestDetails?.caseExternalId;
  }

  reconciliationCaseExternalId = reconciliationCaseExternalId || parameters?.caseExternalId || '';

  const { data: submissionStatusData } = useQuery(GET_SUBMISSION_STATUS, {
    fetchPolicy: 'no-cache',
    pollInterval: currentSubmissionId ? INVENTORY_REQUEST_DETAIL_UI.SUBMISSION_POLL_INTERVAL_MS : 0,
    skip: !currentSubmissionId,
    variables: {
      submissionId: currentSubmissionId || '',
    },
  });

  const { data: submissionStatusByCaseData } = useQuery(GET_SUBMISSION_STATUS_BY_CASE, {
    fetchPolicy: 'no-cache',
    skip: !reconciliationCaseExternalId || !!currentSubmissionId,
    variables: {
      caseExternalId: reconciliationCaseExternalId,
    },
  });

  const [reSubmitInventory] = useMutation(RESUBMIT_ORDER_DETAILS);

  const [setInventoryFavorite, { data: inventoryFavoriteData }] = useMutation(
    CREATE_UPDATE_INVENTORY_FAVORITE
  );

  const isSubmissionPending = submissionState === 'pending';
  const isProcessedOrFailedType =
    type === ORDER_SCREEN_TYPE.PROCESSED || type === ORDER_SCREEN_TYPE.FAILED;

  let inventoryDetail;
  if (isProcessedOrFailedType) {
    inventoryDetail = getOrderDetails?.getOrderDetails;
  } else if (eventInventoryData && externalId === eventInventoryExternalId) {
    inventoryDetail = eventInventoryData?.getInventoryRequestDetails;
  } else {
    inventoryDetail = getInventoryRequestDetails?.getInventoryRequestDetails;
  }

  const showSubmitInFlightMessage = (): void => {
    openSnackBar(NOTIFY_TYPE.INFO, INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_IN_FLIGHT);
  };

  const hasSubmitInFlightErrorCode = (error: unknown): boolean => {
    const graphQLErrors = (error as {
      graphQLErrors?: Array<{ extensions?: { code?: string } }>;
    })?.graphQLErrors;

    if (!Array.isArray(graphQLErrors)) {
      return false;
    }

    return graphQLErrors.some(
      graphQLError =>
        graphQLError?.extensions?.code ===
        INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_IN_FLIGHT_ERROR_CODE
    );
  };

  const isSubmitInFlightBackendError = (error: unknown): boolean => {
    if (hasSubmitInFlightErrorCode(error)) {
      return true;
    }

    const errorMessage = (error as { message?: string })?.message?.toLowerCase() || '';

    // Keep a temporary fallback for environments that do not yet return extensions.code.
    return errorMessage.includes(INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_IN_FLIGHT_MATCHER);
  };

  // canRetry: false → submitRetryBlockedRef is latched to prevent further submit attempts.
  // This covers errors where retrying would be dangerous (e.g. dirty compensation state,
  // permission denied). canRetry: true → submit remains available after the error clears.
  const classifySubmitError = (error: unknown): { message: string; canRetry: boolean } => {
    const errorMessage = (error as { message?: string })?.message?.toLowerCase() || '';
    const networkError = (error as { networkError?: unknown })?.networkError;

    if (errorMessage.includes(INVENTORY_REQUEST_DETAIL_MESSAGES.PERMISSION_DENIED_MATCHER)) {
      return {
        message: INVENTORY_REQUEST_DETAIL_MESSAGES.PERMISSION_DENIED_ON_HOLD,
        canRetry: false,
      };
    }

    if (networkError) {
      return {
        message: INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_NETWORK_ERROR,
        canRetry: true,
      };
    }

    // Option E (#1253330) — compensation-FAILED branch (dirty state):
    //   SF succeeded + PG failed + jsforce.delete() also failed → state is unclean → no retry.
    //   SF succeeded + PG failed + jsforce.delete() succeeded → SF cleaned up → falls through
    //   to the default below (retry safe, same as a plain SF failure).
    // TODO (#1253330): once the backend contract is defined, replace the placeholder matcher
    // with the exact extensions.code (e.g. 'COMPENSATION_FAILED') or message substring emitted
    // when jsforce.delete() itself fails. Only that sub-case must land here.
    if (errorMessage.includes(INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_PG_COMPENSATION_MATCHER)) {
      return {
        message: INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_PG_COMPENSATION_ERROR,
        canRetry: false,
      };
    }

    // Covers: plain SF failure, network-independent server errors, and compensation-succeeded
    // (all retry-safe because no partial state was left on SF).
    return {
      message: INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_ERROR,
      canRetry: true,
    };
  };

  const clearPendingSubmitSuccessToasts = useCallback((): void => {
    if (submitWarningToastTimeoutRef.current) {
      clearTimeout(submitWarningToastTimeoutRef.current);
      submitWarningToastTimeoutRef.current = null;
    }

    if (submitSuccessToastTimeoutRef.current) {
      clearTimeout(submitSuccessToastTimeoutRef.current);
      submitSuccessToastTimeoutRef.current = null;
    }
  }, []);

  const showSubmitSuccessWithWarning = useCallback(
    (warningMsg?: string, successToastKey?: string): void => {
      const normalizedSuccessToastKey =
        successToastKey || `${currentSubmissionId || 'no-submission'}::succeeded`;

      if (lastSuccessToastKeyRef.current === normalizedSuccessToastKey) {
        return;
      }

      lastSuccessToastKeyRef.current = normalizedSuccessToastKey;
      clearPendingSubmitSuccessToasts();

      if (warningMsg && warningMsg !== '') {
        submitWarningToastTimeoutRef.current = setTimeout(() => {
          openSnackBar(NOTIFY_TYPE.WARNING, warningMsg);
        }, INVENTORY_REQUEST_DETAIL_UI.SUBMIT_WARNING_TOAST_DELAY_MS);

        submitSuccessToastTimeoutRef.current = setTimeout(() => {
          openSnackBar(NOTIFY_TYPE.SUCCESS, INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_SUCCESS);
        }, INVENTORY_REQUEST_DETAIL_UI.SUBMIT_SUCCESS_TOAST_DELAY_MS);
        return;
      }

      openSnackBar(NOTIFY_TYPE.SUCCESS, INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_SUCCESS);
    },
    [clearPendingSubmitSuccessToasts, currentSubmissionId, openSnackBar]
  );

  useEffect(() => {
    return () => {
      clearPendingSubmitSuccessToasts();
    };
  }, [clearPendingSubmitSuccessToasts]);

  useEffect(() => {
    if (state) {
      const { caseExternalId, opsAction, externalId: externalIdState } = state;
      if (caseExternalId || externalIdState) {
        if (opsAction) {
          setReprocessQueue(true);
          if (opsAction && opsAction === OPS_ACTION_TYPE.ASSIGN) {
            setViewOnly(true);
          }
        }
      } else {
        setReprocessQueue(false);
      }
    }
  }, [history, state]);

  useEffect(() => {
    if (
      cancelInventoryData &&
      cancelInventoryData.cancelEvent &&
      cancelInventoryData.cancelEvent.message === NOTIFY_TYPE.SUCCESS
    ) {
      setTimeout(() => {
        openSnackBar(NOTIFY_TYPE.SUCCESS, INVENTORY_REQUEST_DETAIL_MESSAGES.CANCEL_SUCCESS);
      }, 1000);
    }

    if (inventoryFavoriteData && inventoryFavoriteData.createUpdateInventoryFavorite) {
      setTimeout(() => {
        openSnackBar(NOTIFY_TYPE.SUCCESS, INVENTORY_REQUEST_DETAIL_MESSAGES.FAVORITE_SUCCESS);
      }, 1000);
      setTimeout(() => {
        history.push(INVENTORY_REQUEST_DETAIL_ROUTES.FAVORITES);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelInventoryData, inventoryFavoriteData, history, openSnackBar]);

  useEffect(() => {
    if (currentSubmissionId) {
      return;
    }

    const byCaseStatus = submissionStatusByCaseData?.getSubmissionStatusByCase;

    if (!byCaseStatus) {
      return;
    }

    const { submissionId } = byCaseStatus;
    const isInFlightStatus = isSubmissionInFlight(byCaseStatus);
    const isRecentInFlight = isRecentSubmission(
      byCaseStatus,
      INVENTORY_REQUEST_DETAIL_UI.SUBMISSION_RECOVERY_MAX_AGE_MS
    );

    if (isInFlightStatus && submissionId && isRecentInFlight) {
      setCurrentSubmissionId(submissionId);
      setSubmissionState('pending');
    }
  }, [currentSubmissionId, submissionStatusByCaseData]);

  useEffect(() => {
    const statusResponse = submissionStatusData?.getSubmissionStatus;
    let needByDate;

    if (isProcessedOrFailedType) {
      needByDate = getOrderDetails?.getOrderDetails?.needByDate;
    } else if (eventInventoryData && externalId === eventInventoryExternalId) {
      needByDate = eventInventoryData?.getInventoryRequestDetails?.needByDate;
    } else {
      needByDate = getInventoryRequestDetails?.getInventoryRequestDetails?.needByDate;
    }

    if (!statusResponse) {
      return;
    }

    const { status, deadLettered, errorMessage, submissionId } = statusResponse;

    if (
      status === INVENTORY_REQUEST_SUBMISSION_STATUS.QUEUED ||
      status === INVENTORY_REQUEST_SUBMISSION_STATUS.PROCESSING
    ) {
      setSubmissionState('pending');
      return;
    }

    if (status === INVENTORY_REQUEST_SUBMISSION_STATUS.SUCCEEDED) {
      setSubmissionState('success');
      setCurrentSubmissionId(null);

      if (refetchEventDetails) {
        refetchEventDetails();
      }
      if (refetchProcessedEventDetails) {
        refetchProcessedEventDetails();
      }

      const warningMsg = warningValidation(
        INVENTORY_REQUEST_DETAIL_UI.REQUESTED_DELIVERY_DATE_LABEL,
        needByDate,
        userInfo
      );

      showSubmitSuccessWithWarning(
        warningMsg,
        `${submissionId || currentSubmissionId || 'no-submission'}::succeeded`
      );
      return;
    }

    if (status === INVENTORY_REQUEST_SUBMISSION_STATUS.REJECTED) {
      setSubmissionState('error');
      setCurrentSubmissionId(null);
      openSnackBar(
        NOTIFY_TYPE.ERROR,
        errorMessage || INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_REJECTED_ERROR
      );
      return;
    }

    if (status === INVENTORY_REQUEST_SUBMISSION_STATUS.ERROR) {
      if (!deadLettered) {
        setSubmissionState('pending');
        return;
      }

      setSubmissionState('error');
      setCurrentSubmissionId(null);
      openSnackBar(
        NOTIFY_TYPE.ERROR,
        errorMessage || INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_ERROR
      );
    }
  }, [
    eventInventoryData,
    eventInventoryExternalId,
    externalId,
    getInventoryRequestDetails,
    getOrderDetails,
    openSnackBar,
    refetchEventDetails,
    refetchProcessedEventDetails,
    submissionStatusData,
    isProcessedOrFailedType,
    userInfo,
    currentSubmissionId,
    showSubmitSuccessWithWarning,
  ]);

  useEffect(() => {
    try {
      dispatch(setUserInfo(data ? data.getUserInfo[0] : {}));
    } catch (err) {
      // do nothing
    }
  }, [dispatch, data]);
  const refetch = async (): Promise<void> => {
    refetchEventDetails();
    refetchProcessedEventDetails();
  };
  const refetchData = async (): Promise<void> => {
    setLoader(true);
    await refetchEventDetails();
    await refetchProcessedEventDetails();
    setLoader(false);
  };
  const refetchOnAdd = (): void => {
    if (type === ORDER_SCREEN_TYPE.FAILED) {
      setCheckStatusActive(true);
      refetchProcessedEventDetails();
    } else {
      refetch();
    }
    setCheckStatusActive(false);
  };

  const handleCancelInventoryModal = (): void => {
    setOpenInventoryCancelModal(!openInventoryCancelModal);
  };

  const cancelInventory = (): void => {
    if (userComment?.length === 0 || userInfo?.id !== userComment?.[0]?.userId) {
      openSnackBar(
        NOTIFY_TYPE.INFO,
        INVENTORY_REQUEST_DETAIL_MESSAGES.COMMENT_REQUIRED_FOR_REJECTION
      );
      setOpenInventoryCancelModal(!openInventoryCancelModal);
      return;
    }
    const mutation = {
      sfid: id,
      externalId: isEventInventoryFlow || hasEventInventory ? eventInventoryExternalId : externalId,
      requestedFrom: inventoryDetail ? inventoryDetail.eventType : '',
      expectedCaseVersion: inventoryDetail && inventoryDetail.caseVersion,
    };
    setCancelInventory({
      variables: mutation,
    })
      .then(() => {
        setOpenInventoryCancelModal(false);
        refetchEventDetails();
        refetchProcessedEventDetails();
      })
      .catch(error => {
        if (isStaleRecordError(error)) {
          openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
        } else openSnackBar(NOTIFY_TYPE.ERROR, 'Error: Inventory cancellation error.');
        // eslint-disable-next-line no-console
        console.log(error); // TODO change when some operation needs to be run on error.
      });
  };

  const setUserEnteredComment = (comment: any): void => {
    setUserComment(comment);
  };

  const setShipMethodValue = (item: string): void => {
    if (isSubmissionPending) {
      showSubmitInFlightMessage();
      return;
    }

    setShipMethod(item);

    if (inventoryDetail) {
      const payload = {
        caseId: inventoryDetail.caseId,
        caseExternalId: inventoryDetail.caseExternalId,
        accountId: inventoryDetail.accountId,
        branchId: inventoryDetail.branchId,
        salesRepId: inventoryDetail.salesRepId,
        shipToId: inventoryDetail.shipToId,
        serviceLevel: inventoryDetail.serviceLevel,
        shipMethod: item || inventoryDetail.shipMethod || '',
        deliveryContractEmail: inventoryDetail.deliveryContractEmail,
        deliveryContractInstructions: inventoryDetail.deliveryContractInstructions,
        deliveryContractName: inventoryDetail.deliveryContractName,
        deliveryContractPhone: inventoryDetail.deliveryContractPhone,
        dockAvailable: inventoryDetail.dockAvailable,
        isShippingDetails: inventoryDetail.isShippingDetails,
        liftGateNeeded: inventoryDetail.liftGateNeeded,
        shortTruckOnly: inventoryDetail.shortTruckOnly,
        requestedFrom: INVENTORY_REQUEST_DETAIL_PAYLOAD.REQUESTED_FROM_INVENTORY,
        expectedCaseVersion: inventoryDetail.caseVersion,
        system: SYSTEM_NAME,
        version: process.env.REACT_APP_VERSION || DEFAULT_VERSION,
      };
      setLoader(true);
      createUpdateUsage({
        variables: {
          ...payload,
        },
      })
        .then((res: any) => {
          setTimeout(() => {
            refetchData().then((_res: any) => {
              if (
                res &&
                res.data &&
                res?.data?.createUpdateUsage?.message === NOTIFY_TYPE.SUCCESS
              ) {
                setTimeout(() => {
                  setLoader(false);
                  openSnackBar(
                    NOTIFY_TYPE.SUCCESS,
                    INVENTORY_REQUEST_DETAIL_MESSAGES.SHIPPING_METHOD_UPDATED_SUCCESS
                  );
                }, 3000);
              }
            });
          });
        })
        .catch((error: any) => {
          setLoader(false);
          if (isSubmitInFlightBackendError(error)) {
            showSubmitInFlightMessage();
            return;
          }
          if (isStaleRecordError(error)) {
            openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
          } else {
            openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_REQUEST_DETAIL_MESSAGES.GENERIC_RETRY);
          }
        });
    }
  };

  const formatLineItemToSubmit = (lineItems: any): any => {
    const finalLineItems: Array<any> = [];
    lineItems.map(
      (item: {
        productId: any;
        productNo: any;
        quantity: any;
        usageExternalId: any;
        usageId: any;
        workflowAction: any;
        baseorCapital: any;
      }): any => {
        finalLineItems.push({
          productId: item.productId,
          productNumber: item.productNo,
          quantity: item.quantity,
          usageExternalId: item.usageExternalId,
          usageId: item.usageId,
          workflowAction: item.workflowAction,
          baseorCapital: item?.baseorCapital,
        });
      }
    );
    return finalLineItems;
  };

  const submitInventory = async (): Promise<void> => {
    if (
      submitInventoryLoading ||
      submitInFlightRef.current ||
      submitRetryBlockedRef.current ||
      isSubmissionPending
    ) {
      return;
    }

    let isInventoryOnHold: any = false;
    inventoryDetail.lineItems.map((item: any): any => {
      if (item?.productOnHold) {
        isInventoryOnHold = true;
      }
    });
    if (isInventoryOnHold === true) {
      openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_REQUEST_DETAIL_MESSAGES.INVENTORY_ON_HOLD);
      return;
    }

    const submitLineItems = formatLineItemToSubmit(inventoryDetail.lineItems);

    const validationPayload = {
      caseId: inventoryDetail.caseId,
      caseExternalId: inventoryDetail.caseExternalId,
      accountId: inventoryDetail.accountId,
      salesRepId: inventoryDetail.salesRepId,
      serviceLevel: inventoryDetail.serviceLevel,
      needByDate: inventoryDetail.needByDate,
      eventType: inventoryDetail.parentEventType,
      lineItems: submitLineItems,
    };

    const mutation = {
      caseId: inventoryDetail.caseId,
      caseExternalId: inventoryDetail.caseExternalId,
      accountId: inventoryDetail.accountId,
      branchId: inventoryDetail.branchId,
      salesRepId: inventoryDetail.salesRepId,
      shipToId: inventoryDetail.shippingId,
      serviceLevel: inventoryDetail.serviceLevel,
      needByDate: inventoryDetail.needByDate,
      shipMethod: inventoryDetail.shipMethod,
      eventType: inventoryDetail.parentEventType,
      lineItems: submitLineItems,
      expectedCaseVersion: inventoryDetail.caseVersion,
    };

    lastSuccessToastKeyRef.current = null;
    clearPendingSubmitSuccessToasts();
    submitInFlightRef.current = true;

    try {
      const validationResponse = await apolloClient.query({
        fetchPolicy: 'no-cache',
        query: GET_VALIDATE_INVENTORY_REQUEST_SUBMIT,
        variables: validationPayload,
      });

      const validationResult: ValidateInventorySubmitResult | undefined =
        validationResponse?.data?.validateInventoryRequestSubmit;

      if (validationResult?.valid === false) {
        const reasonMessage = (validationResult?.reasons || [])
          .map((reason: ValidationReason) => reason?.message)
          .filter(Boolean)
          .join(' ');
        openSnackBar(
          NOTIFY_TYPE.ERROR,
          reasonMessage || INVENTORY_REQUEST_ASYNC_MESSAGES.PRE_SUBMIT_VALIDATION_FALLBACK
        );
        setSubmissionState('error');
        return;
      }

      const res = (await setSubmitInventory({
        variables: mutation,
      })) as SubmitInventoryMutationResult;

      const submitResponse = res?.data?.submitInventoryRequest;
      const nextSubmissionId = submitResponse?.submissionId || null;
      const status = submitResponse?.status;

      if (
        status === INVENTORY_REQUEST_SUBMISSION_STATUS.QUEUED ||
        status === INVENTORY_REQUEST_SUBMISSION_STATUS.PROCESSING
      ) {
        if (nextSubmissionId) {
          setCurrentSubmissionId(nextSubmissionId);
        }
        setSubmissionState('pending');
      } else {
        // Async-only: an accepted submit always returns queued/processing. Any other status
        // (e.g. a synchronous 'succeeded' from a backend not yet on the async-only build) is
        // unexpected. Fail loud and block retry so the user is never left with a silent no-op
        // that would invite a duplicate submission.
        openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_ERROR);
        submitRetryBlockedRef.current = true;
        setSubmissionState('error');
      }
    } catch (error) {
      if (isSubmitInFlightBackendError(error)) {
        showSubmitInFlightMessage();
        setSubmissionState('pending');
        return;
      }

      if (isStaleRecordError(error)) {
        openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
        setSubmissionState('error');
        // eslint-disable-next-line no-console
        console.log(error); // TODO change when some operation needs to be run on error.
        return;
      }

      const { message: errorSnackMessage, canRetry } = classifySubmitError(error);
      openSnackBar(NOTIFY_TYPE.ERROR, errorSnackMessage);
      if (!canRetry) {
        submitRetryBlockedRef.current = true;
      }
      setSubmissionState('error');
      console.log(error); // TODO change when some operation needs to be run on error.
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const formatLineItemToReSubmit = (lineItems: any): any => {
    const finalLineItems: Array<any> = [];
    lineItems.map(
      (item: {
        productId: any;
        productNumber: any;
        quantity: any;
        actualQuantity: any;
        externalId: any;
        lotNumber: any;
        sfid: any;
        unitPrice: any;
        caseUsageExternalId: any;
        priceBookEntryId: any;
        salesUnitOfMeasure: any;
        deliveredDate: any;
        caseUsageId: any;
      }): any => {
        finalLineItems.push({
          caseUsageId: item.caseUsageId,
          caseUsageExternalId: item.caseUsageExternalId,
          priceBookEntryId: item.priceBookEntryId,
          salesUnitOfMeasure: item.salesUnitOfMeasure,
          deliveredDate: item.deliveredDate || null,
          productId: item.productId,
          productNumber: item.productNumber,
          quantity: item.quantity.toString(),
          actualQuantity: item.actualQuantity.toString() || '',
          lotNumber: item.lotNumber || '',
          externalId: item.externalId || null,
          sfid: item.sfid || '',
          unitPrice: item.unitPrice || '',
        });
      }
    );
    return finalLineItems;
  };

  const resubmitInventory = (): void => {
    clearPendingSubmitSuccessToasts();

    const comments = userComment?.map((item: any) => {
      return {
        comment: item.comment,
        postTime: item.postTime,
        commentType: item.commentType,
        externalId: item.commentExternalId,
      };
    });
    let isInventoryOnHold: any = false;
    inventoryDetail.lineItems.map((item: any): any => {
      if (item?.productOnHold === true) {
        isInventoryOnHold = true;
      }
    });
    if (isInventoryOnHold === true) {
      openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_REQUEST_DETAIL_MESSAGES.INVENTORY_ON_HOLD);
      return;
    }

    const mutation = {
      id: inventoryDetail.id,
      caseId: inventoryDetail.caseId,
      caseExternalId: inventoryDetail.caseExternalId,
      externalId: inventoryDetail.externalId,
      shippingId: inventoryDetail.shippingId,
      accountId: inventoryDetail.accountId,
      billingId: inventoryDetail.billingId,
      shipMethod: inventoryDetail.shipMethod?.split('-')?.[0] || '',
      serviceLevel: inventoryDetail.serviceLevel,
      salesRepId: inventoryDetail.salesRepId,
      receivingRepId: inventoryDetail.receivingRepId,
      fromSubInventoryCode: inventoryDetail.fromSubInventoryCode,
      toSubInventoryCode: inventoryDetail.toSubInventoryCode,
      salesOrganization: inventoryDetail.salesOrganization,
      needByDate: inventoryDetail.needByDate,
      comments,
      requestedFrom: INVENTORY_REQUEST_DETAIL_PAYLOAD.REQUESTED_FROM_INVENTORY_REQUEST,
      submitType: INVENTORY_REQUEST_DETAIL_PAYLOAD.SUBMIT_TYPE_RESUBMIT,
      lineItems: formatLineItemToReSubmit(inventoryDetail.lineItems),
      expectedCaseVersion: inventoryDetail.caseVersion,
    };
    reSubmitInventory({
      variables: mutation,
    })
      .then((res: any) => {
        // setLoader(false);
        if (res && res.data && res.data.resubmitOrderDetails?.message === NOTIFY_TYPE.SUCCESS) {
          openSnackBar(NOTIFY_TYPE.SUCCESS, INVENTORY_REQUEST_DETAIL_MESSAGES.RESUBMIT_SUCCESS);
          setTimeout(() => {
            if (reprocessQueue) {
              history.push(INVENTORY_REQUEST_DETAIL_ROUTES.FAILED_REQUESTS);
            } else {
              history.goBack();
            }
          }, 1500);
        }
      })
      .catch(error => {
        if (isSubmitInFlightBackendError(error)) {
          showSubmitInFlightMessage();
          return;
        }

        if (isStaleRecordError(error)) {
          openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
        } else if (
          error?.message
            ?.toLowerCase()
            .includes(INVENTORY_REQUEST_DETAIL_MESSAGES.PERMISSION_DENIED_MATCHER)
        ) {
          openSnackBar(
            NOTIFY_TYPE.ERROR,
            INVENTORY_REQUEST_DETAIL_MESSAGES.PERMISSION_DENIED_ON_HOLD
          );
        } else {
          openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_REQUEST_DETAIL_MESSAGES.RESUBMIT_FAILED_ERROR);
        }
        console.log(error); // TODO change when some operation needs to be run on error.
      });
  };

  const handleRequestClick = (
    parentEventType: any,
    parentCaseExternalId: any,
    parentEventId: any,
    parentOrderid?: any
  ): void => {
    if (window.location.pathname !== INVENTORY_REQUEST_DETAIL_ROUTES.SURGICAL_DETAILS) {
      if (parentEventType?.indexOf('Bill') !== -1) {
        history.push(INVENTORY_REQUEST_DETAIL_ROUTES.ORDERS_USAGE_DETAILS, {
          orderId: parentOrderid,
          type: ORDER_SCREEN_TYPE.PROCESSED,
        });
      } else {
        history.push(INVENTORY_REQUEST_DETAIL_ROUTES.SURGICAL_DETAILS, {
          eventType: parentEventType,
          externalId: parentCaseExternalId,
          id: parentEventId,
        });
      }
    }
  };
  const handleInventoryActionDropdownSelect = (option: any): void => {
    if (!option?.value || option.disabled) {
      return;
    }

    const action = option.value.toLowerCase();
    const isSubmitInFlight =
      submitInventoryLoading ||
      submitInFlightRef.current ||
      submitRetryBlockedRef.current ||
      isSubmissionPending;

    if (isSubmitInFlight) {
      const blockedActions = INVENTORY_REQUEST_DETAIL_ACTIONS.BLOCKED_ACTIONS;

      if (blockedActions.includes(action)) {
        showSubmitInFlightMessage();
        return;
      }
    }

    if (action === INVENTORY_REQUEST_DETAIL_ACTIONS.CANCEL_REQUEST) {
      setOpenInventoryCancelModal(!openInventoryCancelModal);
      return;
    }

    if (action === INVENTORY_REQUEST_DETAIL_ACTIONS.SUBMIT) {
      if (option.disabledReason === INVENTORY_REQUEST_DETAIL_ACTIONS.MISSING_LINE_ITEMS) {
        openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_REQUEST_DETAIL_MESSAGES.MISSING_PARTS_FOR_SUBMIT);
        return;
      }

      submitInventory();
      return;
    }

    if (action === INVENTORY_REQUEST_DETAIL_ACTIONS.RESUBMIT) {
      if (option.disabledReason === INVENTORY_REQUEST_DETAIL_ACTIONS.MISSING_LINE_ITEMS) {
        openSnackBar(
          NOTIFY_TYPE.ERROR,
          INVENTORY_REQUEST_DETAIL_MESSAGES.MISSING_PARTS_FOR_RESUBMIT
        );
        return;
      }

      resubmitInventory();
      return;
    }
    if (action === INVENTORY_REQUEST_DETAIL_ACTIONS.EDIT) {
      setIsEdit(true);
      return;
    }
    if (action === INVENTORY_REQUEST_DETAIL_ACTIONS.CLONE) {
      setIsClone(true);
      return;
    }
    if (action === INVENTORY_REQUEST_DETAIL_ACTIONS.MAKE_FAVORITE) {
      setIsSave(!isSave);
      return;
    }
    if (action === INVENTORY_REQUEST_DETAIL_ACTIONS.DISCREPANCY) {
      userInfo?.businessRules.forEach((br: any) => {
        if (br.rules === INVENTORY_REQUEST_DETAIL_ACTIONS.DISCREPANCY_EMAIL_RULE) {
          if (isProcessedOrFailedType) {
            window.open(
              `mailto: ${br.values}?subject=Inventory Request: ${inventoryDetail.caseNumber}&body=`
            );
          } else {
            window.open(
              `mailto: ${br.values}?subject=Inventory Request: ${inventoryDetail.eventId}&body=`
            );
          }
        }
      });
    }
  };

  const handleSaveName = (e: any): void => setFavName(e);
  const handleSaveModalToggle = (): void => {
    setFavName('');
    setIsSave(!isSave);
  };

  const handlelSaveAsFavoriteModalConfirmPress = (): void => {
    setIsSave(!isSave);
    const shipToName = `${inventoryDetail.shippingAddessLine1},${
      inventoryDetail.shippingAddessLine2 ? inventoryDetail.shippingAddessLine2 : ''
    },${inventoryDetail.shippingCity},${inventoryDetail.shippingState},${
      inventoryDetail.shippingPostalcode
    }`;
    const items: any = [];
    if (inventoryDetail && inventoryDetail.lineItems && inventoryDetail.lineItems.length > 0) {
      inventoryDetail.lineItems.map((el: any) => {
        const obj = {
          part: {
            id: el.productId,
            name: `${el.productNumber ? el.productNumber : el.productNo} ${el.productdescription}`,
          },
          lotNumber: '',
          quantity: el.quantity,
        };
        items.push(obj);
      });
    }
    const finalObject = {
      nameOfFavorite: FavName,
      recordType: INVENTORY_REQUEST_DETAIL_PAYLOAD.RECORD_TYPE_CREATE,
      userId,
      serviceLevel: inventoryDetail.serviceLevel,
      salesRep: {
        name: inventoryDetail.salesRep,
        id: inventoryDetail.salesRepId,
      },
      externalId: inventoryDetail.caseExternalId,
      caseId: inventoryDetail.caseId,
      account: {
        id: inventoryDetail.accountId,
        name: `${inventoryDetail.accountName}-${inventoryDetail.accountNumber}`,
      },
      shipTo: {
        id: inventoryDetail.shippingId,
        name: shipToName,
      },
      lineItems: items,
    };
    setInventoryFavorite({
      variables: finalObject,
    }).catch(error => {
      const errMsg = error.message.split(':')[1] || INVENTORY_REQUEST_DETAIL_MESSAGES.GENERIC_RETRY;
      openSnackBar(NOTIFY_TYPE.ERROR, errMsg);
      console.log(error); // TODO change when some operation needs to be run on error.
    });
  };
  const handleModalToggle = useCallback((): void => {
    setIsEdit(!isEdit);
  }, [isEdit]);
  const handleCloneModalToggle = useCallback((): void => {
    setIsClone(!isClone);
  }, [isClone]);

  const handleCancelButton = (): void => setCancelModalVisible(!cancelModalVisible);
  const handleCancelModal = (): void => setCancelModalVisible(!cancelModalVisible);
  const handleCancelModalConfirmPress = (): void => {
    setCancelModalVisible(!cancelModalVisible);
    if (isEdit) {
      handleModalToggle();
    }
    if (isClone) {
      handleCloneModalToggle();
    }
  };

  const handleEditClick = () => {
    setIsEdit(true);
  };

  const handleBackClick = () => {
    history.goBack();
  };

  const eventId = inventoryDetail ? inventoryDetail.eventId : '';
  const eventType = inventoryDetail ? inventoryDetail.eventType : '';
  const visualIndicator = inventoryDetail ? inventoryDetail.visualIndicator : '';
  const eventStatus =
    eventInventoryData && eventInventoryData?.getInventoryRequestDetails?.parentEventStatus
      ? eventInventoryData.getInventoryRequestDetails.parentEventStatus
      : '';
  return (
    <IconSettings iconPath={INVENTORY_REQUEST_DETAIL_UI.ICON_PATH}>
      <SnackBar open={open} notification={notification} />
      {loading || loader || submitInventoryLoading || processedLoading ? (
        <Spinner
          size="large"
          variant="base"
          assistiveText={{ label: INVENTORY_REQUEST_DETAIL_UI.SPINNER_LOADING_LABEL }}
        />
      ) : (
        <>
          {isSubmissionPending && (
            <div className="slds-notify slds-notify_alert slds-alert_warning" role="status">
              <span className="slds-assistive-text">warning</span>
              <div className="slds-notify__content">
                <p className="slds-text-body_regular">
                  {INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_IN_FLIGHT}
                </p>
              </div>
            </div>
          )}
          {isProcessedOrFailedType ? (
            <>
              <DetailsHeaderComponent
                inventoryReturnDetails={inventoryDetail}
                setShipMethodValue={setShipMethodValue}
                handleEditClick={handleEditClick}
                handleRequestClick={handleRequestClick}
                userInfo={userInfo}
                type={type}
                parentEventStatus={eventStatus}
                handleInventoryActionDropdownSelect={handleInventoryActionDropdownSelect}
                irHeaderCofig={inventoryConfig?.headers}
                handleBackClick={handleBackClick}
                viewOnly={viewOnly || isSubmissionPending}
                isSubmissionInFlight={isSubmissionPending}
              />

              <InventoryRequestDetailCatalog
                refetch={refetch}
                inventoryDetail={inventoryDetail}
                isEventInventoryFlow={isEventInventoryFlow}
                visualIndicator={visualIndicator}
                type={type}
                hasEventInventory={hasEventInventory}
                eventInventoryExternalId={eventInventoryExternalId}
                externalId={externalId}
                changeSelectedTab={changeSelectedTab}
                tabConfig={inventoryConfig}
                refetchOnAdd={refetchOnAdd}
                viewOnly={viewOnly || isSubmissionPending}
              />
              {selectedTabIndex !== 1 && (
                <Comments
                  setUserEnteredComment={setUserEnteredComment}
                  inventoryReturnDetails={inventoryDetail}
                  viewOnly={false}
                  type={type}
                />
              )}
            </>
          ) : (
            <>
              <InventoryRequestDetailHeader
                inventoryDetail={inventoryDetail}
                isEventInventoryFlow={isEventInventoryFlow}
                handleInventoryActionDropdownSelect={handleInventoryActionDropdownSelect}
                handleRequestClick={handleRequestClick}
                hasEventInventory={hasEventInventory}
                type={type}
                parentEventStatus={eventStatus}
                irHeaderCofig={inventoryConfig?.headers}
                isSubmissionInFlight={isSubmissionPending}
              />
              <InventoryRequestDetailCatalog
                refetch={refetch}
                inventoryDetail={inventoryDetail}
                isEventInventoryFlow={isEventInventoryFlow}
                visualIndicator={visualIndicator}
                parentEventStatus={eventStatus}
                type={type}
                hasEventInventory={hasEventInventory}
                eventInventoryExternalId={eventInventoryExternalId}
                externalId={externalId}
                changeSelectedTab={changeSelectedTab}
                tabConfig={inventoryConfig}
                viewOnly={viewOnly || isSubmissionPending}
              />
              <Comments
                inventoryReturnDetails={inventoryDetail}
                setUserEnteredComment={setUserEnteredComment}
                viewOnly={false}
                type=""
              />
              {openInventoryCancelModal && (
                <CancelInventory
                  cancelInventoryModalVisible={openInventoryCancelModal}
                  handleCancelInventoryModal={handleCancelInventoryModal}
                  eventId={eventId}
                  eventType={eventType}
                  cancelInventory={cancelInventory}
                  cancelInventoryLoading={cancelInventoryLoading}
                />
              )}
              {cancelModalVisible && (
                <CancelEventModal
                  isInventory
                  canceModalVisible={cancelModalVisible}
                  handleCancelModal={handleCancelModal}
                  handleCancelModalConfirmPress={handleCancelModalConfirmPress}
                  edit={isEdit}
                />
              )}
            </>
          )}
        </>
      )}
      {isClone && (
        <InventoryRequest
          isClone={isClone}
          isOpen={isClone}
          inventoryDetail={inventoryDetail}
          toggleOpen={handleCloneModalToggle}
          formData={formdata}
          refetch={refetch}
          eventType={eventType || INVENTORY_REQUEST_DETAIL_UI.REQUEST_INVENTORY_EVENT_TYPE}
          handleCancelButton={handleCloneModalToggle}
        />
      )}
      {cancelModalVisible && (
        <CancelEventModal
          isInventory={isClone || true}
          canceModalVisible={cancelModalVisible}
          handleCancelModal={handleCancelModal}
          handleCancelModalConfirmPress={handleCancelModalConfirmPress}
          edit={isEdit}
        />
      )}
      {isSave && (
        <InventoryFavoriteSaveModal
          favName={FavName}
          handleSaveName={handleSaveName}
          showEditConfirmationModal={isSave}
          handleModalToggle={handleSaveModalToggle}
          handlelSaveFavoriteModalConfirmPress={handlelSaveAsFavoriteModalConfirmPress}
        />
      )}
      {isEdit && (
        <InventoryRequest
          isOpen={isEdit}
          inventoryDetail={inventoryDetail}
          toggleOpen={handleModalToggle}
          formData={formdata}
          type={type}
          refetch={refetch}
          eventType={eventType || INVENTORY_REQUEST_DETAIL_UI.REQUEST_INVENTORY_EVENT_TYPE}
          handleCancelButton={handleCancelButton}
        />
      )}
    </IconSettings>
  );
};

export default withRouter(InventoryRequestDetail);
